import { Card } from '../../../molecules/Card/Card';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { CalendarHeatmap, HeatScale } from '../../../organisms/CalendarHeatmap/CalendarHeatmap';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type ActivityByDayAndHourView = {
  /** `Peak window` and `Strongest day`. */
  kpis: KPIMetric[];
  /** Weekday labels, Sunday first (the grid's rows). */
  rowLabels: readonly string[];
  /** Hour labels, midnight first (the grid's columns). */
  colLabels: readonly string[];
  /** `values[weekday][hour]` — engagement over the date range. */
  values: readonly (readonly number[])[];
  /** Whether any cell measured anything — a grid of nothing has no peak. */
  hasPeak: boolean;
  formatValue: (value: number) => string;
};

export type ActivityByDayAndHourCardProps = WidgetBlockProps & {
  view?: ActivityByDayAndHourView;
  /** Opens the conversations behind one cell (weekday row, hour column). */
  onViewDetails?: (row: number, col: number) => void;
  /** The help tooltip, where a page words it differently from the default. */
  helpText?: string;
};

/**
 * Community's heat grid (947:19575), and Overview's (972:19376): engagement
 * per weekday × hour over the date range, chips applied.
 */
export function ActivityByDayAndHourCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
  helpText = 'Engagement patterns by hour and weekday during the selected period. Darker cells show more activity.',
}: ActivityByDayAndHourCardProps) {
  const hasData = view !== undefined && view.hasPeak;
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      emptyLabel="No activity in this window"
      {...cardState(status, hasData, onRetry)}
      /*
       * Renamed from "Activity by day and hour" on the user's instruction
       * (2026-09-23). The component, its file and the widget id keep the old
       * name — they are referenced from saved reports, and renaming the id
       * would orphan every report already holding it.
       */
      title="Content performance"
      helpText={helpText}
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <CalendarHeatmap
            onViewDetails={onViewDetails}
            rowLabels={view.rowLabels}
            colLabels={view.colLabels}
            values={view.values}
            height={PLOT_HEIGHT}
            unit="Engagement"
            legend="none"
            formatValue={view.formatValue}
          />
        )
      }
      legend={<HeatScale />}
    />
  );
}
